//deps
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

//contexts
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { CartContext } from "@cart-client/contexts/CartContext";

//helpers
import { getAddressNudgePresentation, getIrctcIdentifier } from "@cart-client/helpers/Cart";
import { getCurrentLocation, getHaversineDistance } from "@cart-client/helpers/CartAddress";
import { isUserLoggedIn } from "@cart-client/helpers/User";

//hooks
import { useCartRendererXP } from "@cart-client/hooks/useCartRendererXP";
import { useFetchAddressOnClick } from "@cart-client/hooks/useFetchAddressOnClick";

//constants, types and interfaces
import { ADDRESS_NUDGE_TOOLTIP_STATUS, BOOLEAN_AS_STRING } from "@swiggy-private/cart-types";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import {
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    useSessionStorage,
} from "@swiggy-private/checkout-core";
import { Location } from "@swiggy-private/cart-types";
import { ADDRESS_NUDGE_TOOLTIP_SESSION_STORAGE_KEY } from "@cart-client/constants/CartAddress";
import { isEmpty } from "@swiggy-private/js-utils";

export const useAddressNudgeConfig = () => {
    const [currentLocation, setCurrentLocation] = useState<Location | undefined>();

    const { cartAddressState, setCartAddressState } = useContext(CartAddressContext);
    const {
        cartState: { reviewedCart: cartResponse },
    } = useContext(CartContext);

    const isCartStatusSuccess = useMemo(
        () => cartResponse?.statusCode === CART_STATUS_CODES.SUCCESS,
        [cartResponse?.statusCode],
    );
    const { cartAddressNudgeEnabledXp } = useCartRendererXP(cartResponse);
    const { selectedAddress } = useFetchAddressOnClick(
        cartResponse?.data?.cart_total,
        cartResponse?.data?.addresses,
        isCartStatusSuccess,
    );
    //flag to identify if user is logged in
    const userLoggedIn = isUserLoggedIn();
    //flag to identify if address is R1FA
    const isR1FA =
        (GlobalHeaderState.getHeader(SwgyHeaderKeysList.isR1FA) as BOOLEAN_AS_STRING) === BOOLEAN_AS_STRING.TRUE;

    const isIrctcCart = useMemo(() => !isEmpty(getIrctcIdentifier(cartResponse)), [cartResponse]);
    const showAddressWidgetTop = useMemo(() => userLoggedIn && selectedAddress, [userLoggedIn, selectedAddress]);
    //flag to find if nudge is already shown
    const { addressNudgeTooltipStatus } = useMemo(
        () => cartAddressState?.addressNudgeConfig,
        [cartAddressState?.addressNudgeConfig],
    );

    const [isNudgeTooltipShownFlag] = useSessionStorage(ADDRESS_NUDGE_TOOLTIP_SESSION_STORAGE_KEY, false);

    const selectedAddressLocation = useMemo(() => {
        if (!(selectedAddress && selectedAddress?.lat && selectedAddress?.lng)) return undefined;
        return {
            lat: selectedAddress.lat,
            lng: selectedAddress.lng,
        } as Location;
    }, [selectedAddress]);

    //flag to indentify if we can show address nudge
    const showAddressNudge = useMemo(
        () =>
            !!(
                showAddressWidgetTop &&
                isCartStatusSuccess &&
                selectedAddress?.delivery_valid &&
                cartAddressNudgeEnabledXp &&
                isR1FA &&
                !isIrctcCart &&
                selectedAddressLocation &&
                currentLocation &&
                currentLocation.lat &&
                currentLocation.lng
            ),
        [
            cartAddressNudgeEnabledXp,
            currentLocation,
            isCartStatusSuccess,
            isR1FA,
            selectedAddress?.delivery_valid,
            selectedAddressLocation,
            showAddressWidgetTop,
            isIrctcCart,
        ],
    );

    const updateCartAddressStateWithNudgeConfig = useCallback(() => {
        // Calculate haversine distance
        const haversineDistance = getHaversineDistance(currentLocation, selectedAddressLocation);

        // Get presentation config
        const addressNudgePresentationConfig = getAddressNudgePresentation(cartResponse);
        const thresholdDistance =
            addressNudgePresentationConfig?.cart_address_nudge_threshold_distance ?? haversineDistance;
        //TODO: remove this once we have analysed reason for disparity b/w android and ios users viewing nudge in xp.
        recordNREvent({
            eventType: NEWRELIC_TABLES.CART_ADDRESS_NUDGE_CONFIG,
            currentLocation: JSON.stringify(currentLocation),
            isR1FA: isR1FA.toString(),
            haversineDistance: haversineDistance?.toString() || "",
            thresholdDistance: thresholdDistance?.toString() || "",
        });
        // Update cartAddressState based on haversine distance check
        setCartAddressState((prevCartAdrressState) => ({
            ...prevCartAdrressState,
            addressNudgeConfig: {
                ...prevCartAdrressState.addressNudgeConfig,
                addressNudgeTooltipStatus:
                    !isNudgeTooltipShownFlag &&
                    haversineDistance &&
                    thresholdDistance &&
                    haversineDistance > thresholdDistance
                        ? ADDRESS_NUDGE_TOOLTIP_STATUS.SHOW
                        : prevCartAdrressState.addressNudgeConfig.addressNudgeTooltipStatus,
                haversineDistance,
                currentLocation,
                isAddressNudgeXpEnabled: cartAddressNudgeEnabledXp,
                ...addressNudgePresentationConfig,
            },
        }));
    }, [
        cartAddressNudgeEnabledXp,
        cartResponse,
        currentLocation,
        isNudgeTooltipShownFlag,
        isR1FA,
        selectedAddressLocation,
        setCartAddressState,
    ]);

    useEffect(() => {
        //do not call getCurrentLocation nb if nudge is not enabled
        if (!cartAddressNudgeEnabledXp || isIrctcCart) {
            return;
        }
        //function to set user's current location
        const setUserCurrentLocation = async () => {
            const location = await getCurrentLocation();
            setCurrentLocation(location as Location | undefined);
        };
        setUserCurrentLocation();
    }, [cartAddressNudgeEnabledXp, isIrctcCart]);

    useEffect(() => {
        // If address nudge is already shown, no need to proceed
        if (addressNudgeTooltipStatus === ADDRESS_NUDGE_TOOLTIP_STATUS.SHOWN) {
            return;
        }

        //if nudge is showing and user moves to other screen
        if (addressNudgeTooltipStatus === ADDRESS_NUDGE_TOOLTIP_STATUS.SHOW && isNudgeTooltipShownFlag) {
            return setCartAddressState((prevState) => ({
                ...prevState,
                addressNudgeConfig: {
                    ...prevState.addressNudgeConfig,
                    addressNudgeTooltipStatus: ADDRESS_NUDGE_TOOLTIP_STATUS.SHOWN,
                    showAddressNudgeIcon: false,
                },
            }));
        }

        // If showAddressNudge is false update cartAddressState for xp
        if (!showAddressNudge) {
            //TODO: remove this once we have analysed reason for disparity b/w android and ios users viewing nudge in xp.
            recordNREvent({
                eventType: NEWRELIC_TABLES.CART_ADDRESS_NUDGE_CONFIG,
                currentLocation: JSON.stringify(currentLocation),
                isR1FA: isR1FA.toString(),
            });
            return setCartAddressState((prevState) => ({
                ...prevState,
                addressNudgeConfig: {
                    ...prevState.addressNudgeConfig,
                    isAddressNudgeXpEnabled: cartAddressNudgeEnabledXp,
                },
            }));
        }

        updateCartAddressStateWithNudgeConfig();
    }, [
        setCartAddressState,
        cartAddressNudgeEnabledXp,
        addressNudgeTooltipStatus,
        showAddressNudge,
        updateCartAddressStateWithNudgeConfig,
        isNudgeTooltipShownFlag,
        isR1FA,
        currentLocation,
    ]);
};
